import { useMemo } from 'react';

import { TMap } from '@shared/types';
import { TDocument, THeading, buildTreeHeading, getAllAncestors } from '@modules/documents';
import { EItemScreenMode, TResolvedItemsMap } from '@modules/items';
import { TImpactItem } from '../types';
import { TFile } from '@modules/files';
import resolveHeadings from '../helpers/resolveHeadings.helper';

const mergeHeadings = (headings: THeading[]): THeading[] => {
  const seen = new Set<string>();
  const uniqueHeadings: THeading[] = [];

  for (const heading of headings) {
    if (!seen.has(heading.id)) {
      seen.add(heading.id);
      uniqueHeadings.push(heading);
    }
  }

  uniqueHeadings.sort((a, b) => a.numbering.localeCompare(b.numbering));
  return uniqueHeadings;
};

const useImpacts = (
  item: TDocument | TFile,
  allSourceHeadings: THeading[] | undefined,
  resolvedItems: TResolvedItemsMap,
  changedHeadingIds: string[],
  screenMode: EItemScreenMode
): TImpactItem[] => {
  return useMemo(() => {
    // 1) Build a parent map from allSourceHeadings, then expand the changedHeadingIds to include their ancestors.
    //    For example, if the user changed #1.1, then #1 is also effectively changed.
    let expandedChanges = new Set<string>();
    if (changedHeadingIds.length > 0 && allSourceHeadings) {
      const { parentMap } = buildTreeHeading(allSourceHeadings);
      expandedChanges = getAllAncestors(changedHeadingIds, parentMap);
      // Also add the originally changed headings themselves (getAllAncestors only returns parents)
      changedHeadingIds.forEach((id) => expandedChanges.add(id));
    }

    // 2) If this item is a block, we add  all documents containing this block.
    const impacts = [...item.impacts];
    if (item.type === 'DOCUMENT') {
      const doc = item as TDocument;
      if (doc.isBlock) {
        for (const placedBlock of doc.placementsOfBlock) {
          if (placedBlock.versionMode === 'AUTO')
            impacts.push({
              source: {},
              target: {
                itemId: placedBlock.doc,
                headingIds: placedBlock.headingId ? [placedBlock.headingId] : undefined,
              },
            });
        }
      }
    }

    // 3) Check each impact & build a map of impacted items.
    const impactMap: TMap<string, TImpactItem> = {};
    const isPreChangeEntireItem =
      screenMode === EItemScreenMode.PRE_CHANGE_IMPACT_ANALYSIS && expandedChanges.size <= 0;

    for (const { source, target } of impacts) {
      // if we have changed headings, only keep impacts whose source.headingIds intersect with the "expanded" changed set (child + parent):
      if (
        source.headingIds &&
        !source.headingIds.some((srcId) => expandedChanges.has(srcId)) &&
        !isPreChangeEntireItem // pass all impacts for pre-change if no selection was made (i.e. entire item is considered)
      )
        continue;

      // filter out self impacts:
      let targetHeadingIds = target.headingIds ? [...target.headingIds] : undefined;
      if (target.itemId === item._id) {
        if (!targetHeadingIds) continue; // Changing something in the same item must not have impact on the entire item (i.e. on itself)
        if (isPreChangeEntireItem)
          // no self-impacts for "entire item" impact in pre-change mode
          continue;
        // Do not include the chaned headings in the impact
        targetHeadingIds = targetHeadingIds?.filter((id) => !expandedChanges.has(id));
      }

      // Find the resolved item by the target ID
      const targetId = target.itemId;
      const resolvedItem = resolvedItems[targetId];
      if (!resolvedItem) continue;

      const resolvedHeadings: THeading[] | undefined = resolveHeadings(targetHeadingIds, resolvedItem.resolvedHeadings);

      // Merge or create an entry in the impact map
      if (!impactMap[targetId]) {
        impactMap[targetId] = {
          item: resolvedItem.item,
          headings: resolvedHeadings,
        };
      } else {
        if (!targetHeadingIds || !resolvedHeadings) {
          // Entire file is impacted => remove headings
          delete impactMap[targetId].headings;
        } else if (impactMap[targetId].headings) {
          impactMap[targetId].headings = mergeHeadings([...impactMap[targetId].headings, ...resolvedHeadings]);
        }
      }
    }

    return Object.values(impactMap);
  }, [item.impacts, resolvedItems, changedHeadingIds, screenMode, allSourceHeadings]);
};

export default useImpacts;
